// Karlilik raporu — secilen donemde nokta bazli gelir/gider/net dagilimi.

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowDownCircle, ArrowUpCircle, ChevronRight, Coins, Wallet } from 'lucide-react';
import {
  BosDurum,
  Iskelet,
  Kart,
  KartBaslik,
  KartIcerik,
  Rozet,
  TarihAraligiSecici,
  type TarihAraligi,
} from '@emanetx/ui';
import { MASRAF_KATEGORILERI, type MasrafKategori } from '@emanetx/sdk';
import { useKarlilik, useKarlilikTrendi } from '../muhasebe/api.js';
import { MASRAF_KATEGORI_ETIKETI } from '../muhasebe/masraf-dialog.js';
import { NoktaSecici } from '../noktalar/nokta-secici.js';

function sonAy(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setDate(s.getDate() - 29);
  s.setHours(0, 0, 0, 0);
  return { baslangic: s, bitis: e };
}

function paraFmt(n: number | string): string {
  return Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Date → ay yıl (1-12) */
function tarihtenAy(d: Date): { yil: number; ay: number } {
  return { yil: d.getFullYear(), ay: d.getMonth() + 1 };
}

export function SayfaKarlilik() {
  const [aralik, setAralik] = useState<TarihAraligi>(sonAy);
  const [noktaId, setNoktaId] = useState('');

  // Tarih araligini ay'a cevir (backend ay bazli grupluyor)
  const baslangic = aralik.baslangic ?? new Date();
  const bitis = aralik.bitis ?? new Date();
  const bB = tarihtenAy(baslangic);
  const bE = tarihtenAy(bitis);

  const sorgu = {
    yilBaslangic: bB.yil,
    ayBaslangic: bB.ay,
    yilBitis: bE.yil,
    ayBitis: bE.ay,
    ...(noktaId && { noktaId }),
  };

  const karlilik = useKarlilik(sorgu);
  const trend = useKarlilikTrendi(sorgu);
  const navigate = useNavigate();

  const t = karlilik.data?.toplam;
  const kategoriler = t
    ? Object.entries(t.kategoriDagilim).sort((a, b) => Number(b[1]) - Number(a[1]))
    : [];

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Karlılık Raporu</h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          Nokta bazlı gelir/gider/net kâr dağılımı. Gelir: tamamlanmış kiralamalar. Gider: nokta
          masrafları.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <p className="text-fume-500 text-[10px] font-semibold uppercase">Dönem</p>
          <TarihAraligiSecici
            deger={aralik}
            onDegisim={setAralik}
            etiketYok
            className="h-10 text-xs"
          />
        </div>
        <div className="space-y-1">
          <p className="text-fume-500 text-[10px] font-semibold uppercase">Nokta</p>
          <NoktaSecici seciliId={noktaId} onSec={setNoktaId} placeholder="Tüm noktalar" />
        </div>
      </div>

      {karlilik.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Iskelet className="h-28" />
          <Iskelet className="h-28" />
          <Iskelet className="h-28" />
          <Iskelet className="h-28" />
        </div>
      ) : !t ? (
        <BosDurum ikon={Coins} baslik="Veri bulunamadı" />
      ) : (
        <>
          {/* Ozet kartlar */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <OzetKart
              etiket="Toplam Gelir"
              tutar={paraFmt(Number(t.gelirBrut) + Number(t.ceza))}
              alt={`${t.kiralamaSayisi} kiralama${Number(t.ceza) > 0 ? ` · ${paraFmt(t.ceza)}₺ ceza dahil` : ''}`}
              ikon={ArrowUpCircle}
              renk="emerald"
            />
            <OzetKart
              etiket="İade"
              tutar={paraFmt(t.iade)}
              alt="Müşterilere iade"
              ikon={ArrowDownCircle}
              renk="amber"
              negatif
            />
            <OzetKart
              etiket="Toplam Gider"
              tutar={paraFmt(t.gider)}
              alt={`${kategoriler.length} kategori`}
              ikon={Wallet}
              renk="red"
              negatif
            />
            <OzetKart
              etiket="Net Kâr"
              tutar={paraFmt(t.net)}
              alt={Number(t.net) >= 0 ? 'Kâr ediliyor' : 'Zarar var'}
              ikon={Coins}
              renk={Number(t.net) >= 0 ? 'marka' : 'red'}
              guclu
            />
          </div>

          {/* Aylik trend bar grafik */}
          {trend.data && trend.data.length > 0 && (
            <Kart>
              <KartBaslik>
                <h2 className="text-base font-semibold">Aylık Trend</h2>
              </KartBaslik>
              <KartIcerik>
                <TrendGrafik veri={trend.data} />
              </KartIcerik>
            </Kart>
          )}

          <div className="grid gap-4 lg:grid-cols-3">
            {/* Nokta listesi */}
            <Kart className="lg:col-span-2">
              <KartBaslik>
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-semibold">Noktalar</h2>
                  <span className="text-fume-500 text-xs">
                    {karlilik.data?.noktalar.length ?? 0} nokta
                  </span>
                </div>
              </KartBaslik>
              <KartIcerik>
                {(karlilik.data?.noktalar ?? []).length === 0 ? (
                  <BosDurum
                    ikon={Coins}
                    baslik="Bu dönemde gelir/gider yok"
                    aciklama="Dönemi değiştirip tekrar deneyin."
                  />
                ) : (
                  <div className="divide-fume-100 dark:divide-fume-800 divide-y">
                    {karlilik.data!.noktalar.map((n) => (
                      <button
                        key={n.noktaId}
                        onClick={() =>
                          void navigate({
                            to: '/panel/noktalar/$id',
                            params: { id: n.noktaId },
                            search: { tab: 'muhasebe' },
                          })
                        }
                        className="hover:bg-fume-50 dark:hover:bg-fume-800/50 flex w-full items-center gap-4 py-3 text-left transition-colors"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-fume-900 dark:text-fume-50 truncate text-sm font-semibold">
                              {n.noktaAd}
                            </span>
                            <Rozet ton="fume" className="font-mono text-[10px]">
                              {n.noktaKod}
                            </Rozet>
                          </div>
                          <p className="text-fume-500 text-[11px]">
                            {n.kiralamaSayisi} kiralama · {n.masrafSayisi} masraf
                          </p>
                        </div>
                        <div className="flex gap-4 text-right">
                          <Sutun
                            etiket="Gelir"
                            deger={paraFmt(Number(n.gelirBrut) + Number(n.ceza))}
                            renk="emerald"
                          />
                          <Sutun etiket="Gider" deger={paraFmt(n.gider)} renk="red" />
                          <Sutun
                            etiket="Net"
                            deger={paraFmt(n.net)}
                            renk={Number(n.net) >= 0 ? 'marka' : 'red'}
                            guclu
                          />
                        </div>
                        <ChevronRight size={14} className="text-fume-400" />
                      </button>
                    ))}
                  </div>
                )}
              </KartIcerik>
            </Kart>

            {/* Kategori dagilimi */}
            <Kart>
              <KartBaslik>
                <h2 className="text-base font-semibold">Gider Dağılımı</h2>
              </KartBaslik>
              <KartIcerik>
                {kategoriler.length === 0 ? (
                  <p className="text-fume-500 py-4 text-center text-xs">Gider yok</p>
                ) : (
                  <KategoriDagilimi kategoriler={kategoriler} toplam={Number(t.gider)} />
                )}
              </KartIcerik>
            </Kart>
          </div>
        </>
      )}
    </div>
  );
}

function OzetKart({
  etiket,
  tutar,
  alt,
  ikon: Ikon,
  renk,
  negatif,
  guclu,
}: {
  etiket: string;
  tutar: string;
  alt?: string;
  ikon: React.ComponentType<{ size?: number; className?: string }>;
  renk: 'emerald' | 'red' | 'amber' | 'marka';
  negatif?: boolean;
  guclu?: boolean;
}) {
  const renkSinif = {
    emerald: 'text-emerald-600 dark:text-emerald-400',
    red: 'text-red-600 dark:text-red-400',
    amber: 'text-amber-600 dark:text-amber-400',
    marka: 'text-marka-600 dark:text-marka-400',
  }[renk];
  return (
    <Kart>
      <KartIcerik>
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 ${renkSinif}`}>
            <Ikon size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-fume-500 dark:text-fume-400 text-[11px] font-semibold uppercase tracking-wide">
              {etiket}
            </p>
            <p
              className={`mt-0.5 font-mono ${guclu ? 'text-2xl font-bold' : 'text-lg font-semibold'} ${renkSinif}`}
            >
              {negatif && Number(tutar.replace(/\./g, '').replace(',', '.')) > 0 ? '−' : ''}
              {tutar} ₺
            </p>
            {alt && <p className="text-fume-500 dark:text-fume-400 mt-0.5 text-[11px]">{alt}</p>}
          </div>
        </div>
      </KartIcerik>
    </Kart>
  );
}

function Sutun({
  etiket,
  deger,
  renk,
  guclu,
}: {
  etiket: string;
  deger: string;
  renk: 'emerald' | 'red' | 'marka';
  guclu?: boolean;
}) {
  const renkSinif = {
    emerald: 'text-emerald-600 dark:text-emerald-400',
    red: 'text-red-600 dark:text-red-400',
    marka: 'text-marka-600 dark:text-marka-400',
  }[renk];
  return (
    <div className="text-right">
      <p className="text-fume-500 text-[10px] font-semibold uppercase">{etiket}</p>
      <p className={`font-mono text-xs ${guclu ? 'font-bold' : 'font-semibold'} ${renkSinif}`}>
        {deger} ₺
      </p>
    </div>
  );
}

function KategoriDagilimi({
  kategoriler,
  toplam,
}: {
  kategoriler: [string, string][];
  toplam: number;
}) {
  return (
    <ul className="space-y-2">
      {kategoriler.map(([kat, tutar]) => {
        const sayi = Number(tutar);
        const yuzde = toplam > 0 ? (sayi / toplam) * 100 : 0;
        const etiket = MASRAF_KATEGORILERI.includes(kat as MasrafKategori)
          ? MASRAF_KATEGORI_ETIKETI[kat as MasrafKategori]
          : kat;
        return (
          <li key={kat}>
            <div className="mb-1 flex items-center justify-between gap-2 text-xs">
              <span className="text-fume-700 dark:text-fume-200 font-medium">{etiket}</span>
              <span className="text-fume-500 font-mono">
                {paraFmt(tutar)} ₺ · {yuzde.toFixed(1)}%
              </span>
            </div>
            <div className="bg-fume-100 dark:bg-fume-800 h-1.5 overflow-hidden rounded-full">
              <div
                className="h-full bg-red-500/70 dark:bg-red-400/70"
                style={{ width: `${Math.min(100, yuzde)}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function TrendGrafik({
  veri,
}: {
  veri: { ay: string; gelir: string; gider: string; net: string }[];
}) {
  const max = Math.max(...veri.flatMap((v) => [Number(v.gelir), Number(v.gider)]), 1);
  return (
    <div className="space-y-4">
      <div className="grid grid-flow-col gap-3" style={{ gridAutoColumns: 'minmax(0, 1fr)' }}>
        {veri.map((v) => {
          const gelirYuzde = (Number(v.gelir) / max) * 100;
          const giderYuzde = (Number(v.gider) / max) * 100;
          const net = Number(v.net);
          return (
            <div key={v.ay} className="flex flex-col items-center">
              <div className="flex h-32 w-full items-end justify-center gap-1">
                <div
                  className="w-3 rounded-t bg-emerald-500/80 dark:bg-emerald-400/80"
                  style={{ height: `${Math.max(2, gelirYuzde)}%` }}
                  title={`Gelir: ${paraFmt(v.gelir)} ₺`}
                />
                <div
                  className="w-3 rounded-t bg-red-500/80 dark:bg-red-400/80"
                  style={{ height: `${Math.max(2, giderYuzde)}%` }}
                  title={`Gider: ${paraFmt(v.gider)} ₺`}
                />
              </div>
              <p className="text-fume-500 mt-1 font-mono text-[10px]">{v.ay}</p>
              <p
                className={`font-mono text-[10px] font-semibold ${
                  net >= 0 ? 'text-marka-600 dark:text-marka-400' : 'text-red-600 dark:text-red-400'
                }`}
              >
                {paraFmt(v.net)}
              </p>
            </div>
          );
        })}
      </div>
      <div className="flex justify-center gap-4 text-[11px]">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-emerald-500/80 dark:bg-emerald-400/80" />
          <span className="text-fume-500">Gelir</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded bg-red-500/80 dark:bg-red-400/80" />
          <span className="text-fume-500">Gider</span>
        </span>
      </div>
    </div>
  );
}
